// Libraries
import {
    createListenerMiddleware,
    isAnyOf,
    isRejected,
} from '@reduxjs/toolkit';
import { EventSourcePolyfill } from 'event-source-polyfill';

// Models
import { Game, Session } from '../features/session/sessionModels';
import { OtherUser } from '../features/user/userModels';

// Features
import {
    fetchSessionAsync,
    postBingoClaimAsync,
    pushSessionState,
    pushSessionWinners,
    pushGameState,
    pushPlayerList,
    pushWinningPattern,
    clearGame,
    clearSession,
    pushCurrentMacro,
    pushCurrentBanner,
} from '../features/session/sessionSlice';
import {
    clearApiToken,
    clearModalContent,
    getTokenAsync,
    pushModalContent,
    pushSplashMessage,
} from '../features/app/appSlice';
import {
    pushMessage,
    pushMessagesStatus,
    pushNotifiedPlayers,
} from '../features/chat/chatSlice';
import { fetchSessionsAsync } from '../features/lobby/lobbySlice';
import {
    getAccessTokenAsync,
    getUserAsync,
    pushUser,
} from '../features/user/userSlice';

// Utilities
import compareTimes from '../utils/date/compareTimes';
import createGameStartingMessage from '../utils/misc/createGameStartingMessage';
import setAvatar from '../utils/misc/setAvatar';

// Constants
import {
    ERROR_EXPIRED_TOKEN,
    ERROR_GENERIC,
    ERROR_INVALID_TOKEN,
    MODAL_INSUFFICIENT_TOKENS,
    MODAL_GENERIC,
    MODAL_IN_ANOTHER_SESSION,
    MODAL_NO_OPEN_TOKEN,
} from '../utils/constants/constants';

const userMacro: OtherUser = {
    id: 0,
    username: 'Bingoport',
    avatar: `https://${process.env.REACT_APP_CDN}/play-bingo/images/logos/logo-circle.svg`,
};

/**
 * @function createEventSource
 * Creates a new EventSource interface connection
 *
 * @param {Session} session
 * @return {EventSource}
 */
function createEventSource(session: Session) {
    const url = new URL(session.event_source);
    session.topic.forEach((topic) => {
        url.searchParams.append('topic', topic);
    });

    // The callback will be called every time an update is published
    const eventSource = new EventSourcePolyfill(url.toString(), {
        headers: {
            Authorization: `Bearer ${session.token}`,
        },
    });

    return eventSource;
}

/**
 * @const listenerMiddleware
 * https://redux-toolkit.js.org/api/createListenerMiddleware
 */
export const listenerMiddleware = createListenerMiddleware();

/**
 * Store API token in local storage for use in other API calls
 */
listenerMiddleware.startListening({
    actionCreator: getTokenAsync.fulfilled,
    effect: async (action) => {
        localStorage.setItem('apiToken', action.payload.token);
    },
});

/**
 * Clear API token on authentication error
 */
listenerMiddleware.startListening({
    matcher: isRejected,
    effect: async (action, listenerApi) => {
        const error = action.payload;
        const dispatch = listenerApi.dispatch;

        if (error === ERROR_INVALID_TOKEN || error === ERROR_EXPIRED_TOKEN) {
            localStorage.removeItem('apiToken');
            dispatch(clearApiToken());
        }
    },
});

/**
 * Store the user details once received from API
 */
listenerMiddleware.startListening({
    matcher: isAnyOf(fetchSessionsAsync.fulfilled, fetchSessionAsync.fulfilled),
    effect: async (action, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        const user = action.payload.user;

        dispatch(getUserAsync(user.id));
    },
});

/**
 * Determine whether the user can play the current game
 */
// listenerMiddleware.startListening({
//   actionCreator: fetchSessionAsync.fulfilled,
//   effect: async (action, listenerApi) => {
//     const dispatch = listenerApi.dispatch;
//     const session = action.payload.session;

//     if (session.allowEntryMidGame === false) {
//       dispatch(
//         pushGameState({
//           playable: false,
//         })
//       );
//     }
//   },
// });

/**
 * Store message in LocalStorage when user receives a message
 */
listenerMiddleware.startListening({
    actionCreator: pushMessage,
    effect: async (action, listenerApi) => {
        const state: any = listenerApi.getState();
        sessionStorage.setItem('chatHistory', state.chat.messages);
    },
});

/**
 * Open chat history when the user sends a message
 */
listenerMiddleware.startListening({
    actionCreator: pushMessage,
    effect: async (action, listenerApi) => {
        const state: any = listenerApi.getState();
        const dispatch = listenerApi.dispatch;
        if (action.payload.user.id === state.user.user.id) {
            dispatch(pushMessagesStatus(true));
        }
    },
});

/**
 * Reset chat message history when a session is closed
 * NOTE: Not used due to preserve chat when joining next session
 */
// listenerMiddleware.startListening({
//   actionCreator: clearSession,
//   effect: async (action, listenerApi) => {
//     const dispatch = listenerApi.dispatch;
//     dispatch(clearChatHistory());
//   },
// });

/**
 * Close token purchase modal when a token purchase is successful
 */
listenerMiddleware.startListening({
    actionCreator: getAccessTokenAsync.fulfilled,
    effect: async (action, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        dispatch(clearModalContent());
        dispatch(fetchSessionsAsync());
    },
});

/**
 * Update user's access token count after a token purchase
 */
listenerMiddleware.startListening({
    actionCreator: getAccessTokenAsync.fulfilled,
    effect: async (action, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        const user = action.payload.user;

        dispatch(pushUser(user));
    },
});

/**
 * Provide warning modal when a points purchase is unsuccessful
 */
listenerMiddleware.startListening({
    actionCreator: fetchSessionAsync.rejected,
    effect: async (action, listenerApi) => {
        const message = action.payload;
        const dispatch = listenerApi.dispatch;
        switch (message) {
            case 'User in another session':
                dispatch(pushModalContent(MODAL_IN_ANOTHER_SESSION));
                break;
            case 'No access tokens for this session':
                dispatch(pushModalContent(MODAL_NO_OPEN_TOKEN));
                break;
            default:
                dispatch(pushModalContent(MODAL_GENERIC));
        }
    },
});

/**
 * Provide warning modal when a token purchase is unsuccessful
 */
listenerMiddleware.startListening({
    actionCreator: getAccessTokenAsync.rejected,
    effect: async (action, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        dispatch(pushModalContent(MODAL_INSUFFICIENT_TOKENS));
    },
});

/**
 * Send welcome message to user once joined
 */
listenerMiddleware.startListening({
    actionCreator: fetchSessionAsync.fulfilled,
    effect: async (action, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        const sessionName = action.payload.session.name;
        const totalPoints = action.payload.total_points;
        const welcomeMessage =
            action.payload.session.welcome_message !== ''
                ? action.payload.session.welcome_message
                : `Welcome to the ${sessionName} session, where we are playing for up to ${totalPoints} PP`;

        dispatch(
            pushMessage({
                id: action.payload.session.id,
                text: welcomeMessage,
                user: userMacro,
            }),
        );
        dispatch(pushMessagesStatus(true));
    },
});

/**
 * Notify the user they have bingo on a successful manual claim
 */
listenerMiddleware.startListening({
    actionCreator: postBingoClaimAsync.rejected,
    effect: async (action, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        dispatch(pushSplashMessage('You do not have bingo.'));
    },
});

/**
 * Start listening to the Mercure server
 */
listenerMiddleware.startListening({
    actionCreator: fetchSessionAsync.fulfilled,
    effect: async (action, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        const eventSource = createEventSource(action.payload);
        const games: Game[] = action.payload.games;

        eventSource.onmessage = function (response: any) {
            const state: any = listenerApi.getState();
            const uuid = response.lastEventId.replace('urn:uuid:', '');
            const data = JSON.parse(response.data);
            console.log(data);

            switch (data.event) {
                case 'session_open':
                    dispatch(
                        pushSessionState({
                            state: 'open',
                        }),
                    );
                    dispatch(
                        pushMessage({
                            id: uuid,
                            text: `${action.payload.session.name} now starting`,
                            user: userMacro,
                        }),
                    );
                    break;
                case 'session_void':
                    dispatch(
                        pushSessionState({
                            state: 'complete',
                        }),
                    );
                    dispatch(
                        pushMessage({
                            id: uuid,
                            text: 'Session Cancelled. All wagers have been refunded',
                            user: userMacro,
                        }),
                    );
                    break;
                case 'session_complete':
                    dispatch(
                        pushSessionState({
                            state: 'complete',
                        }),
                    );
                    dispatch(
                        pushSessionWinners({
                            gameWinners: data.gameWinners,
                            accumulatedWinners: data.accumulatedWinners,
                        }),
                    );
                    break;
                case 'session_closed':
                    eventSource.close();
                    dispatch(
                        pushSessionState({
                            // state: 'closed',
                        }),
                    );
                    dispatch(
                        pushMessage({
                            id: uuid,
                            text: `${
                                action.payload.session.name
                            } has ended. Please return to the lobby to choose your next ${
                                action.payload.session.games.length > 1
                                    ? 'session'
                                    : 'game'
                            }.`,
                            user: userMacro,
                        }),
                    );
                    break;
                case 'intermission':
                    const nextBanner = state.session.session.banners.length
                        ? state.session.currentBanner ===
                          state.session.session.banners.length - 1
                            ? 0
                            : state.session.currentBanner + 1
                        : 0;
                    const nextMacro = state.session.session.macros.length
                        ? state.session.currentMacro ===
                          state.session.session.macros.length - 1
                            ? 0
                            : state.session.currentMacro + 1
                        : 0;

                    dispatch(pushCurrentBanner(nextBanner));

                    setTimeout(() => {
                        dispatch(
                            pushSessionState({
                                state: 'open',
                            }),
                        );
                        dispatch(clearGame());
                        dispatch(
                            pushGameState({
                                id: data.nextGameId,
                                start: data.nextGameAt,
                            }),
                        );
                        dispatch(
                            pushMessage({
                                id: uuid,
                                text: state.session.session.macros[
                                    state.session.currentMacro
                                ].text,
                                user: userMacro,
                            }),
                        );
                        dispatch(pushCurrentMacro(nextMacro));
                        dispatch(pushMessagesStatus(true));
                    }, 10 * 1000);
                    break;
                case 'game_starting_in':
                    const startTime = new Date(`${data.start}`);
                    const timeDiff = compareTimes(startTime);

                    if (timeDiff <= 60) {
                        dispatch(
                            pushSessionState({
                                state: 'open',
                            }),
                        );
                        dispatch(clearGame());
                        dispatch(
                            pushGameState({
                                id: data.nextGameId,
                                start: data.start,
                            }),
                        );
                    }
                    break;
                case 'start_game':
                    const game = games.find(
                        ({ id }) => id === data.currentGameId,
                    );
                    const message =
                        game !== undefined
                            ? createGameStartingMessage(game, data.current_pot)
                            : 'Next game starting';

                    dispatch(
                        pushSessionState({
                            state: 'open',
                        }),
                    );
                    dispatch(clearGame());
                    dispatch(
                        pushGameState({
                            id: data.currentGameId,
                            currentPot: data.current_pot,
                        }),
                    );
                    dispatch(
                        pushMessage({
                            id: uuid,
                            text: message,
                            user: userMacro,
                        }),
                    );
                    break;
                case 'ball_call':
                    dispatch(
                        pushSessionState({
                            state: 'open',
                        }),
                    );
                    dispatch(
                        pushGameState({
                            state: 'open',
                            id: data.currentGameId,
                            ball: data.ball,
                            call: data.call,
                            called: data.called,
                            currentPot: data.current_pot,
                            patternsWon: data.patternsWon,
                        }),
                    );
                    break;
                case 'win':
                    const gameMeta = games.find(
                        ({ id }) => id === data.currentGameId,
                    );
                    dispatch(
                        pushSessionState({
                            state: 'open',
                        }),
                    );
                    dispatch(
                        pushGameState({
                            id: data.currentGameId,
                            winners: data.winners,
                        }),
                    );

                    if (
                        data.winners.some(
                            (winner: any) =>
                                winner.userId === state.user.user.id,
                        )
                    ) {
                        dispatch(pushSplashMessage("You've got bingo!"));
                    } else {
                        dispatch(pushSplashMessage('We have a winner!'));
                    }

                    data.winners.map(
                        ({
                            userId,
                            username,
                            patternId,
                            points,
                        }: {
                            userId: number;
                            username: string;
                            patternId: number;
                            points: number;
                        }) => {
                            const pattern = gameMeta?.game.gamePatterns.find(
                                ({ pattern }) => pattern.id === patternId,
                            );
                            const message = `${username} has won ${points} PP${
                                pattern !== undefined
                                    ? ` on ${pattern.name}`
                                    : ''
                            }!`;

                            dispatch(
                                pushMessage({
                                    id: `${uuid}-${username}-${patternId}`,
                                    text: message,
                                    user: userMacro,
                                }),
                            );
                            if (userId === state.user.user.id) {
                                dispatch(
                                    pushUser({
                                        points: state.user.user.points + points,
                                    }),
                                );
                            }
                        },
                    );

                    setTimeout(() => {
                        dispatch(pushWinningPattern(data.pattern));
                        dispatch(
                            pushGameState({
                                id: data.currentGameId,
                                state: 'pattern_won',
                                winners: data.winners,
                                manualClaimState: 'pending',
                            }),
                        );
                    }, 2 * 1000);
                    break;
                case 'game_winners':
                    dispatch(
                        pushSessionState({
                            state: 'open',
                        }),
                    );
                    dispatch(
                        pushSessionWinners({
                            gameWinners: [
                                ...state.session.gameWinners,
                                ...data.winners,
                            ],
                            accumulatedWinners: data.accumulatedWinners,
                        }),
                    );
                    setTimeout(() => {
                        dispatch(
                            pushGameState({
                                id: data.currentGameId,
                                state: 'game_won',
                                winners: data.winners,
                            }),
                        );
                    }, 6 * 1000);
                    break;
                case 'claim_successful':
                    dispatch(pushSplashMessage("You've got bingo!"));
                    dispatch(
                        pushGameState({
                            manualClaimState: 'successful',
                        }),
                    );
                    break;
                case 'claim_unsuccessful':
                    dispatch(
                        pushSplashMessage(
                            'Sorry, you claimed too late to win.',
                        ),
                    );
                    dispatch(
                        pushGameState({
                            manualClaimState: 'failed',
                        }),
                    );
                    break;
                case 'balls_exhausted':
                    dispatch(
                        pushSessionState({
                            state: 'open',
                        }),
                    );
                    dispatch(
                        pushGameState({
                            state: 'closed',
                            id: data.currentGameId,
                        }),
                    );
                    break;
                case 'user_entry':
                    if (
                        state.chat.notifiedPlayers.includes(data.username) ===
                        false
                    ) {
                        dispatch(
                            pushMessage({
                                id: `${uuid}-${data.username}`,
                                text: `${data.username} has joined the session.`,
                                user: userMacro,
                            }),
                        );
                        dispatch(pushNotifiedPlayers(data.username));
                    }
                    break;
                case 'players':
                    dispatch(
                        pushPlayerList({
                            players: data.players,
                        }),
                    );
                    data.players.map(({ username }: { username: string }) => {
                        if (
                            state.chat.notifiedPlayers.includes(username) ===
                            false
                        ) {
                            dispatch(pushNotifiedPlayers(username));
                        }
                    });
                    break;
                case 'chat':
                    dispatch(
                        pushMessage({
                            id: uuid,
                            text: data.message,
                            user: {
                                id: data.user_id,
                                username: data.username,
                                avatar: setAvatar(data.avatar),
                            },
                        }),
                    );
                    break;
                case 'macro':
                    dispatch(
                        pushMessage({
                            id: uuid,
                            text: data.message,
                            user: userMacro,
                        }),
                    );
                    break;
            }
        };

        // Close the Mercure channel when a user leaves the session
        if (await listenerApi.condition(clearSession.match)) {
            eventSource.close();
        }
    },
});
